import { ArrowDownIcon, LockIcon, RouteIcon, UsersIcon } from 'lucide-react';
import { BrandLogo } from '../alt/AppChrome';
type TrackCard = {
  id: string;
  name: string;
  group: string;
  count: number;
  shared: boolean;
  observations: Array<{
    logo: string;
    text: string;
    fresh?: boolean;
  }>;
};
const sourceGroups = [{
  label: 'Scans',
  note: 'On a schedule',
  caption: 'Sweeps your tools on a schedule.',
  logos: ['gmail', 'salesforce', 'notion', 'intercom', 'linear']
}, {
  label: 'Team chats',
  note: 'As people work',
  caption: 'Adds what it learns from conversations.',
  logos: ['slack', 'microsoftteams', 'giga', 'chatgpt', 'claude']
}] as const;
const gridPositions = ['14%', '32%', '50%', '68%', '86%'];
const tracks: TrackCard[] = [{
  id: 'northwind',
  name: 'Northwind Labs',
  group: 'Sales',
  count: 412,
  shared: true,
  observations: [{
    logo: 'notion',
    text: 'Champion unchanged after the pilot review'
  }, {
    logo: 'salesforce',
    text: 'Opportunity in Legal review · $84k'
  }]
}, {
  id: 'onboarding',
  name: 'Onboarding revamp',
  group: 'Customer Success',
  count: 268,
  shared: true,
  observations: [{
    logo: 'intercom',
    text: 'Setup is the top blocker, 31 conversations'
  }, {
    logo: 'linear',
    text: 'CSV import bug shipped Tuesday'
  }]
}, {
  id: 'pipeline',
  name: 'Q3 pipeline',
  group: 'Marketing',
  count: 631,
  shared: true,
  observations: [{
    logo: 'gmail',
    text: 'Four renewal threads quiet over a week',
    fresh: true
  }, {
    logo: 'slack',
    text: 'Discount ceiling agreed at 15%'
  }]
}, {
  id: 'board',
  name: 'Board reporting',
  group: 'Leadership',
  count: 88,
  shared: false,
  observations: [{
    logo: 'salesforce',
    text: 'Q3 forecast down one slipped deal'
  }]
}];

/**
 * A square crop of the existing Sources → Tracks Company Brain instrument.
 * The center Tracks remain fully readable while faded edge Tracks imply the
 * larger set without forcing four narrow columns into a 1:1 frame.
 */
export function TrackIngestSquare() {
  return <section aria-label="Scans and team chats becoming observations in Tracks" className="relative aspect-square w-full overflow-hidden rounded-[24px] border border-white/[0.14] bg-[#1C1511] text-white shadow-[0_28px_70px_-42px_rgba(43,33,26,0.9)]" data-id="element-3516" data-mp="14y834y2y7l17">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.035]" data-id="element-3517" data-mp="1sky4rybdxdla">
        {gridPositions.map(position => <span key={`v-${position}`} className="absolute inset-y-0 w-px bg-white" style={{
        left: position
      }} data-id="element-3518" data-mp="1ivmwuf08ieyz" />)}
        {gridPositions.map(position => <span key={`h-${position}`} className="absolute inset-x-0 h-px bg-white" style={{
        top: position
      }} data-id="element-3519" data-mp="1ett02ht5tv43" />)}
      </div>

      <header className="relative z-10 flex h-11 items-center justify-between border-b border-white/10 px-5" data-id="element-3520" data-mp="26l8xz806349m">
        <span className="flex items-center gap-2 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white/60" data-id="element-3521" data-mp="3p88rci3ulvxb">
          <span className="size-1.5 rounded-full bg-[#FB923C]" data-id="element-3522" data-mp="3u6aaf319xv8s" />
          Ingest live
        </span>
        <span className="text-[9.5px] tabular-nums text-white/40" data-id="element-3523" data-mp="22zvdjffs1fv">1,399 observations kept</span>
      </header>

      <div className="relative z-10 px-5 pb-5 pt-7" data-id="element-3524" data-mp="73fc8kyxfr2l">
        <div className="grid grid-cols-2 gap-6" data-id="element-3525" data-mp="c1gsky4yxcad">
          {sourceGroups.map(group => <div key={group.label} className="flex min-w-0 flex-col items-center" data-id="element-3526" data-mp="bzmfg5zxuogu">
              <div className="relative w-full rounded-full border border-dashed border-white/20 px-3 pb-2.5 pt-3.5" data-id="element-3527" data-mp="gyfjz8x8z54v">
                <p className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#1C1511] px-2 text-[9.5px] font-bold uppercase tracking-[0.11em] text-white/55" data-id="element-3528" data-mp="ltmkk7v7ztpk">
                  {group.label}
                  <span className="ml-1.5 font-medium normal-case tracking-normal text-white/30" data-id="element-3529" data-mp="zqrwgohm7msq">{group.note}</span>
                </p>
                <div className="flex items-center justify-center gap-2" data-id="element-3530" data-mp="m39e3wk4chhx">
                  {group.logos.map(logo => <span key={logo} className="flex size-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-[#FAF8F4]" data-id="element-3531" data-mp="h4ojqdrjss1e">
                      <BrandLogo slug={logo} className="size-[18px]" data-id="element-3532" data-mp="qvaspp4rekb4" />
                    </span>)}
                </div>
              </div>
              <p className="mt-2.5 text-center text-[10.5px] text-white/45" data-id="element-3533" data-mp="35oxtdvf6r8x">{group.caption}</p>
            </div>)}
        </div>

        <div className="relative h-[100px]" aria-label="A new observation is filed into the Q3 pipeline Track" data-id="element-3534" data-mp="d3cw2ds8igvu">
          <span aria-hidden="true" className="absolute left-1/4 top-0 h-7 w-px bg-white/15" data-id="element-3535" data-mp="849zlhqblizh" />
          <span aria-hidden="true" className="absolute right-1/4 top-0 h-7 w-px bg-white/15" data-id="element-3536" data-mp="1oi9uufe80kge" />
          <span aria-hidden="true" className="absolute left-1/4 right-1/4 top-7 h-px bg-white/15" data-id="element-3537" data-mp="1jk82f3uwn8s9" />
          <span aria-hidden="true" className="absolute left-1/2 top-7 h-4 w-px -translate-x-1/2 bg-white/15" data-id="element-3538" data-mp="1r2oeemqcdubu" />
          <div className="absolute left-1/2 top-10 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-[#FAF8F4] bg-[#FAF8F4] px-3 py-2 text-[#2B211A] shadow-[0_12px_28px_-14px_rgba(0,0,0,0.7)]" data-id="element-3539" data-mp="1w2kguhje5dpa">
            <BrandLogo slug="slack" className="size-3.5" data-id="element-3540" data-mp="1m5616k5aksy1" />
            <span className="whitespace-nowrap text-[10px] font-semibold" data-id="element-3541" data-mp="29tp7wv8p0xo0">Discount ceiling agreed at 15%</span>
          </div>
          <ArrowDownIcon aria-hidden="true" size={13} className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[#FB923C]" data-id="element-3542" data-mp="2et1vji7e28rn" />
        </div>

        <div className="mb-2.5 flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.1em] text-white/45" data-id="element-3543" data-mp="21158hskkup24">
          <RouteIcon size={11} strokeWidth={2.2} data-id="element-3544" data-mp="2ssl3ob079k3y" />
          Tracks
        </div>

        <div className="relative -mx-5 h-[310px] overflow-hidden" data-id="element-3545" data-mp="2sqgp8z4od2pf">
          <div className="absolute left-1/2 top-0 grid w-[880px] -translate-x-1/2 grid-cols-4 gap-3" data-id="element-3546" data-mp="2xk35t2dylqqi">
            {tracks.map((track, index) => {
            const edge = index === 0 || index === tracks.length - 1;
            const highlighted = track.id === 'pipeline';
            return <article key={track.id} className={`h-[296px] rounded-2xl border p-3.5 ${edge ? 'opacity-25' : 'opacity-100'} ${highlighted ? 'border-[#FB923C]/35 bg-white/[0.075]' : 'border-white/10 bg-white/[0.05]'}`} data-id="element-3547" data-mp="2ydah364ky30j" data-mpi={index}>
                  <div className="flex items-start gap-2" data-id="element-3548" data-mp="2jpfodpqknyng">
                    <RouteIcon size={13} strokeWidth={1.9} className="mt-0.5 shrink-0 text-white/40" data-id="element-3549" data-mp="2jnut1xwfoboj" />
                    <div className="min-w-0 flex-1" data-id="element-3550" data-mp="2flq6fj4ekfbe">
                      <p className="truncate text-[12.5px] font-semibold text-white" data-id="element-3551" data-mp="2aqaqmlmu49cc">{track.name}</p>
                      <p className="mt-1 flex items-center gap-1.5 truncate text-[10px] text-white/45" data-id="element-3552" data-mp="25olgd87frzpb">
                        {track.shared ? <UsersIcon size={9} data-id="element-3553" data-mp="20rlhpicx1z88" /> : <LockIcon size={9} data-id="element-3554" data-mp="1qmwsv86fpwyi" />}
                        {track.group}
                      </p>
                    </div>
                  </div>

                  <p className="mt-3 flex items-baseline gap-1.5 border-t border-white/10 pt-3" data-id="element-3555" data-mp="1vkeqw3iwyrrq">
                    <span className="text-[20px] font-semibold leading-none tabular-nums text-white" data-id="element-3556" data-mp="20llzg4svruzg">{track.count}</span>
                    <span className="text-[9.5px] text-white/40" data-id="element-3557" data-mp="25i2yhu1mbtrb">observations</span>
                  </p>

                  <ul className="mt-3 space-y-2" data-id="element-3558" data-mp="17uark8crde6j">
                    {track.observations.map(observation => <li key={observation.text} className={`flex min-h-[54px] items-start gap-2 rounded-xl border px-2.5 py-2.5 ${observation.fresh ? 'border-[#FAF8F4] bg-[#FAF8F4]' : 'border-white/10 bg-[#4A403A]'}`} data-id="element-3559" data-mp="1hul4druvsjl6">
                        <BrandLogo slug={observation.logo} onDark={!observation.fresh} className="mt-0.5 size-3.5 shrink-0" data-id="element-3560" data-mp="p42zclalk854" />
                        <span className={`text-[10.5px] leading-[1.35] ${observation.fresh ? 'font-medium text-[#2B211A]' : 'text-white/70'}`} data-id="element-3561" data-mp="28axfu7i7y7z9">
                          {observation.text}
                        </span>
                      </li>)}
                  </ul>
                </article>;
          })}
          </div>
        </div>
      </div>
    </section>;
}